import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, CalendarDays, MapPin, Mail, ArrowRight, Zap } from "lucide-react";

const POLL_MAX = 8;
const POLL_DELAY_MS = 1500;

export default function BookingConfirmed() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { setUser } = useAuth();
  const [status, setStatus] = useState("polling");  // polling | paid | failed
  const [details, setDetails] = useState(null);     // {offering, booking}
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sid) { setStatus("failed"); return; }
    let cancelled = false;
    const tick = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          // Refresh user (assessment_status / tracking_active may have shifted)
          try {
            const me = await api.get("/auth/me");
            setUser(me.data);
          } catch (_e) { /* ignore */ }
          // Load offering details for the receipt
          if (data.offering_id) {
            try {
              const off = await api.get(`/offerings/${data.offering_id}`);
              setDetails({ offering: off.data });
            } catch (_e) { /* ignore */ }
          }
          setStatus("paid");
          return;
        }
        if (n + 1 < POLL_MAX) {
          setTimeout(() => { setTries(n + 1); tick(n + 1); }, POLL_DELAY_MS);
        } else {
          setStatus("failed");
        }
      } catch (e) {
        if (cancelled) return;
        // 404 means the session id doesn't exist for this user
        setStatus("failed");
        setDetails({ error: formatApiError(e.response?.data?.detail) || "Could not verify payment" });
      }
    };
    tick(0);
    return () => { cancelled = true; };
     
  }, [sid]);

  if (status === "polling") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg flex items-center justify-center p-6" data-testid="booking-polling">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-[#262626] border-t-[#CCFF00] animate-spin" />
          <div className="label-mono mb-2 text-[#CCFF00]">CONFIRMING WITH STRIPE</div>
          <div className="text-sm text-[#A3A3A3]">Hang tight — we&apos;re finalising your booking. Attempt {tries + 1} of {POLL_MAX}.</div>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg flex items-center justify-center p-6" data-testid="booking-failed">
        <div className="card-surface p-8 max-w-md text-center">
          <h1 className="h-display text-3xl mb-2">We couldn&apos;t confirm your payment.</h1>
          <p className="text-sm text-[#A3A3A3] mb-6">{details?.error || "Stripe didn't confirm in time. If your card was charged, the booking will appear in your dashboard within a few minutes — otherwise please try again."}</p>
          <Link to="/offerings" className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2">
            Back to offerings <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  const o = details?.offering;
  const when = o?.start ? new Date(o.start) : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg flex items-center justify-center p-6" data-testid="booking-confirmed">
      <div className="w-full max-w-xl">
        <Link to="/" className="flex items-center gap-2 mb-8 hover:opacity-80 transition-opacity w-fit">
          <div className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center"><Zap strokeWidth={2.5} className="w-5 h-5 text-black" /></div>
          <span className="h-display text-2xl tracking-tighter">RALLY LAB</span>
        </Link>

        <div className="card-surface p-8 border-l-4 border-[#CCFF00]">
          <div className="w-14 h-14 bg-[#CCFF00] flex items-center justify-center mb-5">
            <Check className="w-7 h-7 text-black" strokeWidth={3} />
          </div>
          <div className="label-mono mb-2" style={{ color: "#CCFF00" }}>PAYMENT CONFIRMED</div>
          <h1 className="h-display text-4xl tracking-tighter mb-4" data-testid="confirmed-title">You&apos;re booked in.</h1>

          {o && (
            <div className="space-y-3 border-y border-[#262626] py-5 mb-5">
              <div className="h-display text-2xl tracking-tight">{o.title}</div>
              <div className="flex items-center gap-2 text-sm text-[#E5E5E5]">
                <CalendarDays className="w-4 h-4 text-[#CCFF00]" />
                {when && when.toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })} · {o.duration_min || 60} min
              </div>
              <div className="flex items-center gap-2 text-sm text-[#E5E5E5]">
                <MapPin className="w-4 h-4 text-[#CCFF00]" />
                {o.venue}
              </div>
              <div className="text-sm text-[#A3A3A3]">With <span className="text-white">{o.coach_name}</span> · paid <span className="text-white">£{o.price_per_player}</span></div>
            </div>
          )}

          <div className="flex items-start gap-2 text-xs text-[#A3A3A3] mb-6">
            <Mail className="w-4 h-4 text-[#CCFF00] mt-0.5 shrink-0" />
            We&apos;ve emailed a confirmation to your inbox. Your booking also lives in <span className="text-white">Upcoming sessions</span> on your dashboard.
          </div>

          <div className="flex gap-3 flex-wrap">
            <Link to="/dashboard" data-testid="confirmed-dashboard" className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2 flex-1 justify-center">
              Go to my dashboard <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/offerings" data-testid="confirmed-more" className="px-5 py-3 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] inline-flex items-center gap-2">
              Book another
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
